/**
 * The Statement scales, as numbers for inline styles and arithmetic.
 *
 * Colours are not here: they change with the theme and the accent, so they
 * live as `--st-*` custom properties in `statement.css`, and inline styles
 * reach them with `var(--st-ink3)`. Everything here is theme-independent, and
 * `statement.css` repeats each value as a custom property for CSS rules —
 * `tokens.test.ts` keeps the two copies equal.
 *
 * DESIGN.md says which step to use where. A size that isn't here is a design
 * decision; add the step deliberately rather than writing a literal.
 */

/** Interface text, in px. IBM Plex Sans; figures in IBM Plex Mono (`.st-num`). */
export const textSize = {
  /** Chart axes, tiny badges. */
  micro: 10,
  /** Uppercase section labels, table headers. */
  caption: 11,
  /** Field labels, secondary lines, meta. */
  small: 12,
  /** The default: buttons, rows, controls. */
  body: 12.5,
  /** Running text, emphasised rows, menus. */
  medium: 13,
  /** Card headings in sans, prominent values in a row. */
  large: 14,
  /** The largest sans size. */
  title: 16,
} as const;

/** Display text, in px. IBM Plex Sans, or a big tabular figure. */
export const displaySize = {
  /** A stat's value; small serif headings inside a card. */
  sm: 22,
  /** Card titles — the default for `Display`. */
  md: 25,
  /** Page, modal and standalone-page titles. */
  lg: 30,
  /** The one headline number on a page. */
  xl: 44,
} as const;

/** Corner radius, in px. */
export const radii = {
  /** Bars, marks and other data shapes. */
  hair: 3,
  /** Small chips, skeleton lines, swatches. */
  sm: 8,
  /** Form fields, inner panels, avatars-as-squares. */
  md: 12,
  /** Menus, popovers, nested cards. */
  lg: 16,
  /** Modals. */
  xl: 20,
  /** Cards: squarer than pills, fields and modals. */
  card: 10,
  /** Buttons, pills, round avatars. */
  full: 999,
} as const;

/** Spacing, in px, on a 4px grid. Use for gap, padding and margin in new code. */
export const space = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
} as const;

export const weight = {
  regular: 400,
  medium: 500,
  semibold: 600,
} as const;
